import React, { useState, useCallback, useEffect } from 'react';
import { FiSearch, FiChevronDown, FiPlus, FiCheck, FiX } from 'react-icons/fi';
import { useFeedbackPanel } from '../../context/FeedbackPanelContext';
import '../../styles/MarketplacePage.css';
interface McpIntegration {
    id: string;
    name: string;
    description: string;
    url: string;
    tools: string;
    headers: string;
    hasHeaders: boolean;
    createdBy: string;
    createdAt: string;
    needsUserCredentials: boolean;
    isSystem: boolean;
}
const McpServerIcon: React.FC = () => (<svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="3" width="16" height="6" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="2" y="11" width="16" height="6" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="5.5" cy="6" r="1" fill="currentColor"/>
    <circle cx="5.5" cy="14" r="1" fill="currentColor"/>
  </svg>);
const McpServersPage: React.FC = () => {
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState<'recent' | 'oldest' | 'name'>('recent');
    const [openMenu, setOpenMenu] = useState<string | null>(null);
    const [menuPos, setMenuPos] = useState<{
        top: number;
        left: number;
    } | null>(null);
    const [integrations, setIntegrations] = useState<McpIntegration[]>([]);
    const [loading, setLoading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
    const { setFeedbackPanel } = useFeedbackPanel();
    const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };
    const fetchIntegrations = useCallback(async () => {
        setLoading(true);
        try {
            const { listExternalMcp, dedupeExternalMcps } = await import('../../services/marketplaceService');
            const data = dedupeExternalMcps(await listExternalMcp());
            setIntegrations(data.map(item => ({
                id: item.transactionId,
                name: item.name,
                description: item.description,
                url: item.url,
                tools: item.tools || '[]',
                headers: item.headers || '[]',
                hasHeaders: item.hasHeaders ?? false,
                createdBy: [item.firstName, item.lastName].filter(Boolean).join(' ') || '—',
                createdAt: item.created
                    ? new Date(item.created).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—',
                needsUserCredentials: item.needsUserCredentials ?? false,
                isSystem: item.isSystem ?? false,
            })));
        }
        catch {
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { fetchIntegrations(); }, [fetchIntegrations]);
    const filtered = integrations
        .filter(i => i.name.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => {
        if (sort === 'name')
            return a.name.localeCompare(b.name);
        if (sort === 'oldest')
            return integrations.indexOf(a) - integrations.indexOf(b);
        return integrations.indexOf(b) - integrations.indexOf(a);
    });
    const toastColor = toast?.type === 'success' ? '#10b981' : '#ef4444';
    return (<div style={{ padding: '24px 32px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {toast && (<div style={{
            position: 'fixed', top: 20, right: 20, zIndex: 10001,
            background: `${toastColor}18`,
            border: `1px solid ${toastColor}44`,
            borderRadius: 10, padding: '10px 16px',
            color: toastColor, fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}>
          {toast.type === 'success' ? <FiCheck size={14}/> : <FiX size={14}/>}
          {toast.msg}
        </div>)}
      
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 10, marginBottom: 16 }}>
        <div className="mkt-page__search-wrap" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0 }}>
          <FiSearch size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}/>
          <input className="mkt-page__search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search integrations..."/>
        </div>
        <div className="mkt-page__search-wrap" onClick={() => setSort(s => s === 'recent' ? 'oldest' : s === 'oldest' ? 'name' : 'recent')} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none', marginBottom: 0 }}>
          <FiChevronDown size={13} style={{ color: 'var(--text-tertiary)' }}/>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
            {sort === 'recent' ? 'Recent' : sort === 'oldest' ? 'Oldest' : 'Name'}
          </span>
        </div>
        <button onClick={() => setFeedbackPanel({ type: 'add-mcp', onSuccess: fetchIntegrations })} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexShrink: 0,
            padding: '0 18px', borderRadius: 'var(--radius-sm, 8px)', border: 'none', cursor: 'pointer',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            color: '#fff', fontWeight: 600, fontSize: 13,
            boxShadow: '0 2px 12px rgba(139,92,246,0.35)',
        }}>
          <FiPlus size={14}/> Add integration
        </button>
      </div>

      
      <div className="mcp-table" style={{ flex: 1, overflowY: 'auto' }}>
        <div className="mcp-table__header">
          <div className="mcp-table__header-cell">Name</div>
          <div className="mcp-table__header-cell">Created by</div>
          <div className="mcp-table__header-cell">Date created <FiChevronDown size={11}/></div>
          <div />
        </div>

        {loading ? (<div style={{ padding: '36px 18px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
            Loading…
          </div>) : filtered.length === 0 ? (<div style={{ padding: '36px 18px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
            No integrations found.
          </div>) : filtered.map((item) => (<div key={item.id} className="mcp-table__row" onClick={() => setFeedbackPanel({
                type: 'view-mcp',
                transactionId: item.id,
                name: item.name,
                description: item.description,
                url: item.url,
                tools: item.tools,
                headers: item.headers,
                hasHeaders: item.hasHeaders,
                createdBy: item.createdBy,
                createdAt: item.createdAt,
            })}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              <div className="mcp-icon-box">
                <McpServerIcon />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {item.name}
                  {item.isSystem && (<span title="Managed by Momentum AMP — can't be removed" style={{
                    fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', background: 'rgba(148,163,184,0.12)',
                    border: '1px solid rgba(148,163,184,0.3)', borderRadius: 6, padding: '1px 6px', flexShrink: 0,
                }}>
                      System
                    </span>)}
                  {item.needsUserCredentials && (<span title="You need to set up your personal credentials for this tool" style={{
                    fontSize: 11, fontWeight: 600, color: '#fbbf24', background: 'rgba(245,158,11,0.12)',
                    border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, padding: '1px 6px', flexShrink: 0,
                }}>
                      🔑 Needs credentials
                    </span>)}
                </div>
                <div style={{ fontSize: 12, color: 'var(--accent-cyan, #22d3ee)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', opacity: 0.8 }}>
                  {item.url}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>
              {item.createdBy}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              {item.createdAt}
            </div>
            <div>
              <button className="mcp-dots-btn" onClick={e => {
                e.stopPropagation();
                if (openMenu === item.id) {
                    setOpenMenu(null);
                    setMenuPos(null);
                }
                else {
                    const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                    setMenuPos({ top: rect.bottom + 4, left: rect.right - 140 });
                    setOpenMenu(item.id);
                }
            }}>
                •••
              </button>
            </div>
          </div>))}
      </div>

      
      {openMenu && menuPos && (() => {
            const item = integrations.find(i => i.id === openMenu);
            if (!item)
                return null;
            return (<>
            <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => { setOpenMenu(null); setMenuPos(null); }}/>
            <div className="mcp-dropdown" style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }} onClick={e => e.stopPropagation()}>
              {[
                'View',
                'Edit',
                'Copy URL',
                item.isSystem ? null : 'Delete',
              ].filter((action): action is string => action !== null).map(action => (<button key={action} className={`mcp-dropdown__item${action === 'Delete' ? ' mcp-dropdown__item--delete' : ''}`} onClick={() => {
                        if (action === 'View') {
                            setFeedbackPanel({
                                type: 'view-mcp',
                                transactionId: item.id,
                                name: item.name,
                                description: item.description,
                                url: item.url,
                                tools: item.tools,
                                headers: item.headers,
                                hasHeaders: item.hasHeaders,
                                needsUserCredentials: item.needsUserCredentials,
                                createdBy: item.createdBy,
                                createdAt: item.createdAt,
                            });
                        }
                        else if (action === 'Edit') {
                            setFeedbackPanel({
                                type: 'add-mcp',
                                editData: {
                                    transactionId: item.id,
                                    name: item.name,
                                    description: item.description,
                                    url: item.url,
                                    tools: item.tools,
                                    headers: item.headers,
                                    hasHeaders: item.hasHeaders,
                                    needsUserCredentials: item.needsUserCredentials,
                                },
                                onSuccess: fetchIntegrations,
                            });
                        }
                        else if (action === 'Copy URL') {
                            navigator.clipboard.writeText(item.url).catch(() => { });
                        }
                        else if (action === 'Delete') {
                            setDeleteConfirm(item.id);
                        }
                        setOpenMenu(null);
                        setMenuPos(null);
                    }}>
                  {action}
                </button>))}
            </div>
          </>);
        })()}

      
      {deleteConfirm && (() => {
            const target = integrations.find(i => i.id === deleteConfirm);
            return (<div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setDeleteConfirm(null)}>
            <div className="mcp-delete-dialog" onClick={e => e.stopPropagation()}>
              <h3 className="mcp-delete-dialog__title">Delete MCP Server?</h3>
              <p className="mcp-delete-dialog__body">
                Are you sure you want to delete <strong>{target?.name}</strong>? This action cannot be undone.
              </p>
              <div className="mcp-delete-dialog__actions">
                <button className="mcp-delete-dialog__cancel" onClick={() => setDeleteConfirm(null)}>
                  Cancel
                </button>
                <button className="mcp-delete-dialog__confirm" disabled={deleting} onClick={async () => {
                    setDeleting(true);
                    try {
                        const { deleteExternalMcp } = await import('../../services/marketplaceService');
                        const result = await deleteExternalMcp(deleteConfirm);
                        setDeleteConfirm(null);
                        if (result.success) {
                            showToast('MCP server deleted.');
                        }
                        else {
                            showToast("This integration is managed by Momentum AMP and can't be removed.", 'error');
                        }
                        fetchIntegrations();
                    }
                    catch {
                        setDeleteConfirm(null);
                        showToast('Failed to delete MCP server. Please try again.', 'error');
                    }
                    finally {
                        setDeleting(false);
                    }
                }}>{deleting ? 'Deleting…' : 'Delete'}</button>
              </div>
            </div>
          </div>);
        })()}
    </div>);
};
export default McpServersPage;
